<template>
  <v-container fluid>

    <!-- Page Header -->
    <v-row class="mb-4" align="center">
      <v-col>
        <div class="d-flex align-center">
          <v-avatar color="success" size="52" class="mr-3 elevation-2">
            <v-icon color="white" size="28">mdi-chart-areaspline</v-icon>
          </v-avatar>
          <div>
            <div class="text-h5 font-weight-bold grey--text text--darken-2">Estados Financieros</div>
            <div class="text-caption grey--text">Estado de resultados y situación financiera del período seleccionado</div>
          </div>
        </div>
      </v-col>
      <v-col cols="auto" class="d-flex align-center">
        <v-btn :loading="loadingData" @click="generateData(1)" color="primary" elevation="2"
          class="mr-2 text-none" :disabled="!selected_type">
          <v-icon left>mdi-play</v-icon>
          Generar
        </v-btn>
        <v-menu v-if="treeData.length > 0" bottom origin="center center" transition="scale-transition">
          <template v-slot:activator="{ on, attrs }">
            <v-btn color="success" outlined v-bind="attrs" v-on="on" class="text-none">
              <v-icon left small>mdi-cloud-download</v-icon>
              Exportar
            </v-btn>
          </template>
          <v-list dense class="rounded-lg">
            <v-list-item @click="generateData(2)">
              <v-list-item-icon class="mr-3">
                <v-icon color="success">mdi-file-excel</v-icon>
              </v-list-item-icon>
              <v-list-item-title class="font-weight-medium">Excel (.xlsx)</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-col>
    </v-row>

    <!-- Filter panel -->
    <v-card outlined class="rounded-xl mb-4 pa-4">
      <!-- Row 1: main filters -->
      <v-row align="end" dense>
        <v-col cols="12" sm="6" md="2">
          <div class="text-subtitle-2 mb-1 grey--text text--darken-1">Tipo de Reporte</div>
          <v-autocomplete v-model="selected_type" :items="typeStatus" @change="treeData = []"
            label="Seleccionar estado" item-text="name" prepend-inner-icon="mdi-list-status" item-value="id"
            outlined dense hide-details></v-autocomplete>
        </v-col>
        <v-col cols="12" sm="6" md="2">
          <div class="text-subtitle-2 mb-1 grey--text text--darken-1">Unidad de Negocio</div>
          <v-autocomplete v-model="s_business" :items="s_business_list" label="Unidad de negocio"
            item-text="text" item-value="value" @change="s_onBusinessChange"
            prepend-inner-icon="mdi-office-building-outline" outlined dense hide-details></v-autocomplete>
        </v-col>
        <v-col cols="12" sm="6" md="2">
          <div class="text-subtitle-2 mb-1 grey--text text--darken-1">Sucursal</div>
          <v-autocomplete v-model="s_location" :items="s_locations_list" label="Sucursal"
            item-text="text" item-value="value" @change="s_onLocationChange"
            prepend-inner-icon="mdi-map-marker-outline" outlined dense hide-details></v-autocomplete>
        </v-col>
        <v-col cols="12" sm="6" md="2">
          <div class="text-subtitle-2 mb-1 grey--text text--darken-1">Ejercicio Fiscal</div>
          <v-autocomplete v-model="current_calendar" :items="calendars" label="Calendario" item-text="name"
            prepend-inner-icon="mdi-calendar" item-value="accounting_calendar_id" @change="showPeriods"
            outlined dense hide-details></v-autocomplete>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <div class="text-subtitle-2 mb-1 grey--text text--darken-1">Periodo Contable</div>
          <v-autocomplete v-model="current_period" :items="periods" label="Selecciona un periodo"
            item-text="name" return-object @change="treeData = []" prepend-inner-icon="mdi-sitemap-outline"
            item-value="id" outlined dense hide-details>
            <template v-slot:selection="data">
              <span v-html="data.item.name"></span>
            </template>
            <template v-slot:item="data">
              <div v-html="data.item.name"></div>
            </template>
            <template v-slot:no-data>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title class="red--text">Selecciona primero un calendario</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-autocomplete>
        </v-col>
      </v-row>

      <!-- Row 2: options -->
      <v-row align="center" dense class="mt-3">
        <v-col cols="auto">
          <v-switch v-model="include_closing" label="Incluir cierre" hide-details dense color="primary"
            class="mt-0"></v-switch>
        </v-col>
        <v-col cols="auto">
          <v-switch v-model="convert_transaction" @change="treeData = []"
            :label="convert_transaction ? 'Convertir moneda' : 'Moneda base'"
            hide-details dense color="primary" class="mt-0"></v-switch>
        </v-col>
        <v-col v-if="convert_transaction" cols="12" sm="6" md="2">
          <v-autocomplete dense outlined v-model="selectedCurrecies" :items="currencies" label="Moneda Destino"
            color="primary" prepend-inner-icon="mdi-currency-usd" item-text="code" item-value="id"
            @change="treeData = []" hide-details>
            <template #item="{ item }">
              <span>
                <country-flag :country="getFlag(item.id)" size="small" />
                {{ item.code }}
              </span>
            </template>
          </v-autocomplete>
        </v-col>
      </v-row>
    </v-card>

    <!-- Main content card -->
    <v-card v-if="selected_type" outlined class="rounded-xl">

      <!-- Column header toolbar -->
      <v-toolbar flat dense class="rounded-t-xl px-4">
        <span class="text-caption font-weight-bold grey--text text-uppercase">Cuenta</span>
        <v-spacer></v-spacer>
        <template v-if="selected_type == 1">
          <span class="text-caption font-weight-bold primary--text text-uppercase"
            style="min-width:130px; text-align:right">
            Monto Actual{{ currencyCode ? ` (${currencyCode})` : '' }}
          </span>
          <div style="width:1px; height:16px; background:#e0e0e0" class="mx-3"></div>
        </template>
        <span class="text-caption font-weight-bold success--text text-uppercase"
          style="min-width:130px; text-align:right">
          Monto Acumulado{{ currencyCode ? ` (${currencyCode})` : '' }}
        </span>
      </v-toolbar>
      <v-divider></v-divider>

      <!-- Loading -->
      <div v-if="loadingData" class="text-center py-12">
        <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
        <div class="text-body-2 grey--text mt-3">Generando reporte...</div>
      </div>

      <!-- Empty state -->
      <div v-else-if="treeData.length === 0" class="text-center py-12">
        <v-icon size="72" color="grey lighten-1">mdi-chart-areaspline</v-icon>
        <div class="text-h6 grey--text mt-3">Sin datos para mostrar</div>
        <div class="text-body-2 grey--text mt-1">Selecciona los filtros y presiona Generar</div>
        <v-btn color="primary" outlined class="mt-4 text-none" @click="generateData(1)" :loading="loadingData">
          <v-icon left small>mdi-play</v-icon>
          Generar reporte
        </v-btn>
      </div>

      <!-- Treeview -->
      <v-treeview
        v-else
        hoverable
        return-object
        class="mt-0 trial-tree"
        :activatable="false"
        :items="treeData"
        item-key="id"
        :open.sync="open"
      >
        <template v-slot:prepend="{ item }">
          <v-icon small :color="item.children && item.children.length > 0 ? 'success' : 'grey'">
            {{ item.children && item.children.length > 0 ? 'mdi-folder-outline' : 'mdi-circle-small' }}
          </v-icon>
        </template>

        <template v-slot:label="{ item }">
          <span class="success--text font-weight-bold trial-link" @click.stop="ViewAccount(item)">
            {{ item.code }}
          </span>
          <span class="ml-1 grey--text text--darken-2"
            :class="{ 'font-weight-bold': item.level === 0 }">{{ item.name }}</span>
        </template>

        <template v-slot:append="{ item }">
          <div class="d-flex align-center">
            <template v-if="selected_type == 1">
              <span
                class="font-weight-bold text-right"
                style="min-width:130px"
                :class="(monthTotal(item) * (item.nature || 1)) < 0 ? 'red--text' : (monthTotal(item) !== 0 ? 'primary--text' : 'grey--text text--lighten-1')"
              >
                {{ (monthTotal(item) * (item.nature || 1)) < 0 ? `(${currencyFormat(Math.abs(monthTotal(item)))})` : (monthTotal(item) !== 0 ? currencyFormat(Math.abs(monthTotal(item) * (item.nature || 1))) : '—') }}
              </span>
              <div style="width:1px; height:20px; background:#e0e0e0" class="mx-3"></div>
            </template>
            <span
              class="font-weight-bold text-right"
              style="min-width:130px"
              :class="(yearTotal(item) * (item.nature || 1)) < 0 ? 'red--text' : (yearTotal(item) !== 0 ? 'success--text' : 'grey--text text--lighten-1')"
            >
              {{ (yearTotal(item) * (item.nature || 1)) < 0 ? `(${currencyFormat(Math.abs(yearTotal(item)))})` : (yearTotal(item) !== 0 ? currencyFormat(Math.abs(yearTotal(item) * (item.nature || 1))) : '—') }}
            </span>
          </div>
        </template>
      </v-treeview>

    </v-card>

    <v-dialog v-model="dialog" max-width="500px">
      <v-card>
        <v-card-title>
          <span class="text-h5 primary--text">{{ formTitle }}</span>
          <v-spacer></v-spacer>
          <v-icon @click="dialog = false">close</v-icon>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text>
          <v-container>
            <v-row class="pa-4 pb-4">
              <span><b>Pertenece a {{ this.seletecName }}</b></span>
            </v-row>
            <v-row class="mt-4" no-gutters>
              <v-col cols="12" md="12">
                <v-text-field outlined v-model="editedItem.name" label="Nombre cuenta"></v-text-field>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="12" md="12">
                <v-text-field outlined v-model="editedItem.code" label="Codigo de la cuenta"></v-text-field></v-col>
            </v-row>
            <v-row no-gutters>
              <v-col cols="12" md="12">
                <v-textarea outlined label="Descripcion" v-model="editedItem.note"></v-textarea>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-autocomplete v-model="selectedCategoryDgii" :items="categoryDgii" color="cyanText" return-object
                label="Dgii" outlined required></v-autocomplete>
            </v-row>

            <v-row no-gutters>
              <v-checkbox v-model="editedItem.status" :label="editedItem.status ? 'Activo' : 'Inactivo'"></v-checkbox>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions class="mt-n10">
          <v-row no-gutters justify="center">
            <v-spacer></v-spacer>
            <v-btn outlined text> Cancelar </v-btn>
            <v-spacer></v-spacer>

            <v-spacer></v-spacer>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from "moment";
import { generateEstadoResultadoExcel } from "../../../helpers/estadoResultadoExcel";
import { generateEstadoResultadoView } from "../../../helpers/estadoResultadoView";
import { generateEstadoSituacionExcel } from "../../../helpers/EstadoSituacionExcel";
import { generateEstadoSituacionPDF } from "../../../helpers/EstadoSituacionPdf";
import { generateEstadoSituacionView } from "../../../helpers/estadoSituacionView";

moment.locale("es");

export default {
  data: () => ({
    selectedCategoryDgii: "",
    categoryDgii: [],
    seletecName: "",
    treeData: [],
    open: [],
    active: [],
    convert_transaction: false,
    headers: [
      { text: "Código cuenta", value: "code" },
      { text: "Nombre cuenta", value: "name" },
      { text: "Monto Actual", value: "total_month" },
      { text: "Monto Acumulado", value: "total_year" },
    ],
    dialog: false,
    selectedCurrecies: null,
    items: [],
    dataStatus: [],
    includeCierreAnual: false,
    includeSaldoCero: false,
    can_edit: false,
    loadingData: false,
    selected_type: null,
    periods: [],
    currencies: [],
    calendars: [],
    itemsBussines: [],
    itemsLocations: [],
    itemsLocations2: [],
    itemsDepartment: [],
    itemSaved: [],
    ategoryDgii: [],
    lastData: null,
    s_business: null,
    s_location: 0,
    s_locations_list: [{ text: 'Todas', value: 0 }],
    accounts: [],
    current_calendar: null,
    current_period: null,
    newItem: [],
    itemsRoutes: [],
    include_closing: false,
    editedItem: [],
    netMonthTotal: 0,
    netYearTotal: 0,
    typeStatus: [
      { id: 1, name: "Estado de resultados" },
      { id: 2, name: "Estado de situación financiera" },
    ],
    catalogue_id: null,
    editedIndex: -1,
    filters: { business: "", location: null, department_id: "", routes: "" },
    editedIndex: -1,
  }),
  created() {
    this.getData();
  },
  computed: {
    formTitle() {
      return this.editedIndex === -1
        ? "Crear cuenta contable "
        : "Editar cuenta contable ";
    },
    currencyCode() {
      if (!this.selectedCurrecies || !this.currencies.length) return '';
      const c = this.currencies.find(x => x.id == this.selectedCurrecies);
      return c ? c.code : '';
    },
    s_business_list() {
      const units = this.$store.state.auth.bussines_units || [];
      return [{ text: 'Todas', value: 0 }, ...units];
    },
  },
  watch: {},
  methods: {
    s_onBusinessChange() {
      this.s_location = 0;
      const allLocations = this.$store.state.auth.locations || [];
      this.s_locations_list = [
        { text: 'Todas', value: 0 },
        ...allLocations.filter(l => l.bus_id === this.s_business),
      ];
      this.changeBusiness({ business: this.s_business, location: 0 });
    },
    s_onLocationChange() {
      this.changeBusiness({ business: this.s_business, location: this.s_location || 0 });
    },
    monthTotal(item) {
      return Object.values(item.monthTotals || {}).reduce((a, b) => a + b, 0);
    },
    yearTotal(item) {
      return Object.values(item.yearTotals || {}).reduce((a, b) => a + b, 0);
    },
    ViewAccount(item) {
      const query = {
        account_name: item.name,
        account_code: item.code,
        has_children: item.children && item.children.length > 0 ? '1' : '0',
        business: this.lastData?.business || '',
        location: this.lastData?.location || 0,
      };
      if (this.current_period) {
        query.dateStart = moment(this.current_period.date_from).format('YYYY-MM-DD');
        query.dateEnd = moment(this.current_period.date_to).format('YYYY-MM-DD');
        query.calendar_id = this.current_calendar;
        query.period_num = this.current_period.accounting_period_id;
      }
      const route = this.$router.resolve({
        name: 'ViewEntryDiaryBook',
        params: { id: item.id },
        query,
      });
      window.open(route.href, '_blank');
    },
    async getData() {
      // let access = this.$store.state.auth.user.type_access.forEach(
      //   (element) => {
      //     if (element.access.acces_profile == 14) {
      //       this.can_edit = true;
      //     }
      //   }
      // );
      this.currencies = await this.g_currencies();
      let resultCategoryDgii = await this.api_Get("accounting/category_dgii");
      const dataCategoryDgii = await resultCategoryDgii.json();

      for (let item of dataCategoryDgii) {
        this.categoryDgii.push({ text: item.name, value: item.id });
      }
      this.CategoryDgii = dataCategoryDgii;

      let resultTypeAccounts = await this.api_Get("accounting/accounts");
      const dataTypeAccounts = await resultTypeAccounts.json();
      this.accounts = dataTypeAccounts;
      let child = [];
      for (let items of dataTypeAccounts) {
        if (items.parent_id == null) {
        }
        child.push({
          id: items.id,
          name: items.name,
          parent: items.parent_id,
          code: items.code,
          note: items.note,
          account_type_id: items.account_type_id,
          dgii_category_id: items.dgii_category_id,
          status: items.status,
        });
      }

      const nest = (items, id = null, link = "parent") =>
        items
          .filter((item) => item[link] === id)
          .map((item) => ({ ...item, children: nest(items, item.id) }));

      let result = nest(child);

      this.items = result;
    },
    async getCalendars(filters) {
      if (filters.business == null || filters.business == 0) {
        return;
      }
      this.calendars = [];

      const data = await this.api_Post("accounting/calendar_periods_revaluate", {
        id: filters.business,
      });
      let result = await data.json();
      this.calendars = result.sort((a, b) => a.accounting_calendar_id - b.accounting_calendar_id);

      if (this.calendars.length > 0) {
        this.current_calendar = this.calendars[this.calendars.length - 1].accounting_calendar_id;
        this.showPeriods(this.current_calendar);
      }

      this.lastData = filters;
    },
    showPeriods(id) {
      this.periods = [];
      this.loading = true;
      let data = this.calendars.find((element) => element.accounting_calendar_id == id);
      let per = [];
      data.periods.forEach((element) => {
        let name =
          `<span style="color:blue;">Periodo: ${element.accounting_period_id}</span> - ` +
          moment(element.date_from).format("DD-MMMM-YYYY").toString() +
          " - " +
          moment(element.date_to).format("DD-MMMM-YYYY").toString();
        per.push({ name: name, ...element });
      });
      per.sort((a, b) => a.accounting_period_id - b.accounting_period_id);

      this.periods = per;
      this.loading = false;
    },
    async generateData(type) {
      if (this.current_period == null) {
        this.$toast.warning("Debes seleccionar un periodo");
        return;
      }

      this.loadingData = true;

      let locations = [];
      let combinedYearTotals = {};
      if (this.lastData.location == 0 || this.lastData.location == null) {
        const filteredLocations = this.$store.state.auth.locations.filter(
          (location) =>
            this.$store.state.auth.user.acess_locations.includes(location.id) &&
            location.bus_id == this.lastData.business
        );
        filteredLocations.forEach((location) => {
          locations.push(location.id);
        });
      } else {
        locations.push(this.lastData.location);
      }
      if (type == 1) {
        let data = {
          business: this.lastData.business,
          locations: locations,
          period_id: this.current_period.id,
          convert_to: this.convert_transaction,
          currency_to: this.selectedCurrecies,
          from_date: moment(this.current_period.date_from).format("YYYY-MM-DD"),
          end_date: moment(this.current_period.date_to).format("YYYY-MM-DD"),
          has_close_year: this.include_closing,
        };

        const request = await this.api_Post("accounting/get_estado_resultado", data);
        this.dataStatus = await request.json();
      }

      this.loadingData = false;
      let child = [];
      if (this.selected_type == 1) {
        for (let items of this.accounts) {
          // Verificar si el parent_id es null y si el id es 5, 6 o 7
          if (items.parent_id === null && [5, 6, 7, 449, 451].includes(items.id)) {
            // Agregar el item a la lista child
            child.push({
              id: items.id,
              name: items.name,
              parent: items.parent_id,
              code: items.code,
              note: items.note,
              account_type_id: items.account_type_id,
              dgii_category_id: items.dgii_category_id,
              status: items.status,
            });
          }
          // Agregar también los elementos que no tienen parent_id null
          else if (items.parent_id !== null) {
            child.push({
              id: items.id,
              name: items.name,
              parent: items.parent_id,
              code: items.code,
              note: items.note,
              account_type_id: items.account_type_id,
              dgii_category_id: items.dgii_category_id,
              status: items.status,
            });
          }
        }
      }
      if (this.selected_type == 2) {
        for (let items of this.accounts) {
          // Verificar si el parent_id es null y si el id es 5, 6 o 7
          if (items.parent_id === null && [2, 3, 4].includes(items.id)) {
            // Agregar el item a la lista child
            child.push({
              id: items.id,
              name: items.name,
              parent: items.parent_id,
              code: items.code,
              note: items.note,
              account_type_id: items.account_type_id,
              dgii_category_id: items.dgii_category_id,
              status: items.status,
            });
          }
          // Agregar también los elementos que no tienen parent_id null
          else if (items.parent_id !== null) {
            child.push({
              id: items.id,
              name: items.name,
              parent: items.parent_id,
              code: items.code,
              note: items.note,
              account_type_id: items.account_type_id,
              dgii_category_id: items.dgii_category_id,
              status: items.status,
            });
          }
        }
      }

      const nest = (items, id = null, link = "parent") =>
        items
          .filter((item) => item[link] === id)
          .map((item) => ({ ...item, children: nest(items, item.id) }));

      let result = nest(child);
      let data_business = this.$store.state.auth.bussines_units.find(
        (x) => x.id == this.lastData.business
      );

      let info = {
        date_from: moment(this.current_period.date_from).format("DD-MM-YYYY"),
        date_to: moment(this.current_period.date_to).format("DD-MM-YYYY"),
        business_name: data_business.description_long,
        currency_code: this.currencies.find((x) => x.id == this.selectedCurrecies).code,
      };

      const cloneResult = () => JSON.parse(JSON.stringify(result));

      if (this.selected_type == 1) {
        this.treeData = generateEstadoResultadoView(cloneResult(), this.dataStatus);
        // this.calculateNetTotalsResultado();
      }
      if (this.selected_type == 2) {
        let resultChild = [];

        for (let items of this.accounts) {
          // Verificar si el parent_id es null y si el id es 5, 6 o 7
          if (items.parent_id === null && [5, 6, 7, 449, 451].includes(items.id)) {
            // Agregar el item a la lista child
            resultChild.push({
              id: items.id,
              name: items.name,
              parent: items.parent_id,
              code: items.code,
              note: items.note,
              account_type_id: items.account_type_id,
              dgii_category_id: items.dgii_category_id,
              status: items.status,
            });
          }
          // Agregar también los elementos que no tienen parent_id null
          else if (items.parent_id !== null) {
            resultChild.push({
              id: items.id,
              name: items.name,
              parent: items.parent_id,
              code: items.code,
              note: items.note,
              account_type_id: items.account_type_id,
              dgii_category_id: items.dgii_category_id,
              status: items.status,
            });
          }
        }
        const nestResult = (items, id = null, link = "parent") =>
          items
            .filter((item) => item[link] === id)
            .map((item) => ({ ...item, children: nest(items, item.id) }));

        let resultNew = nestResult(resultChild);

        let neto = generateEstadoResultadoView(resultNew, this.dataStatus);
        const resultadoNetoObj = neto.find((account) => account.name === "Resultado Neto");
        combinedYearTotals = resultadoNetoObj ? resultadoNetoObj.yearTotals : {};

        for (let key in combinedYearTotals) {
          combinedYearTotals[key] = -combinedYearTotals[key];
        }

        this.treeData = generateEstadoSituacionView(
          cloneResult(),
          this.dataStatus,
          combinedYearTotals
        );
      }

      if (type == 2) {
        if (this.selected_type == 1) {
          generateEstadoResultadoExcel(
            cloneResult(),
            this.dataStatus,
            info,
            this.$store.state.auth.all_locations
          );
        }
        if (this.selected_type == 2) {
          generateEstadoSituacionExcel(
            cloneResult(),
            this.dataStatus,
            info,
            this.$store.state.auth.all_locations,
            combinedYearTotals
          );
        }
      }
      if (type == 3) {
        if (this.selected_type == 1) {
          generateEstadoResultadoExcel(
            cloneResult(),
            this.dataStatus,
            info,
            this.$store.state.auth.all_locations
          );
        }
        if (this.selected_type == 2) {
          generateEstadoSituacionPDF(
            cloneResult(),
            this.dataStatus,
            info,
            this.$store.state.auth.all_locations,
            combinedYearTotals
          );
        }
      }
    },
    calculateNetTotalsResultado() {
      const calculateTotals = (nodes) => {
        return nodes.reduce(
          (totals, node) => {
            return totals;
          },
          { monthTotal: 0, yearTotal: 0 }
        );
      };

      const totals = calculateTotals(this.treeData);

      this.netMonthTotal = totals.monthTotal;
      this.netYearTotal = totals.yearTotal;
    },

    generatePDFContent(doc, data) {
      let y = 10;
      const lineHeight = 10;
      const pageHeight = doc.internal.pageSize.height;
      const pageWidth = doc.internal.pageSize.width;
      const marginRight = 10; // Right margin for the PDF
      const amountText = "5,200 USD";
      const maxTextWidth =
        pageWidth -
        marginRight -
        (doc.getStringUnitWidth(amountText) * doc.internal.getFontSize()) /
        doc.internal.scaleFactor -
        20; // -20 for some padding
      const headerLines = [
        "Estado de resultados",
        "40224067260",
        "Del 1 de Junio del 2024 al 30 de Junio del 2024",
        "Valores expresados en DOLAR USD",
      ];

      headerLines.forEach((line) => {
        doc.text(line, pageWidth / 2, y, { align: "center" });
        y += lineHeight;
      });

      y += 5; // Extra space after header
      data.forEach((row) => {
        if (y > pageHeight - lineHeight) {
          // If the line exceeds the page height
          doc.addPage();
          y = 10; // Reset line height
        }
        const indent = " ".repeat(row.level * 4);
        let text = `${indent}${row.code} - ${row.name}`;

        // Truncate text if it exceeds maxTextWidth
        let textWidth =
          (doc.getStringUnitWidth(text) * doc.internal.getFontSize()) /
          doc.internal.scaleFactor;
        if (textWidth > maxTextWidth) {
          while (textWidth > maxTextWidth) {
            text = text.slice(0, -1);
            textWidth =
              (doc.getStringUnitWidth(text + "...") * doc.internal.getFontSize()) /
              doc.internal.scaleFactor;
          }
          text = text + "...";
        }

        doc.text(text, 10, y);

        // Position for the "5,200 USD" text at the right end of the page
        const positionX =
          pageWidth -
          (doc.getStringUnitWidth(amountText) * doc.internal.getFontSize()) /
          doc.internal.scaleFactor -
          marginRight;

        doc.text(amountText, positionX, y);
        y += lineHeight;
      });
    },
    async getCurrencies() {
      const resultCurrencies = await this.api_Post("accounting/currenciesBusiness", {
        business: this.lastData.business,

        effective_date: moment(this.current_period.date_to).format("DD-MM-YYYY"),
      });
      let result = resultCurrencies.json();
      return result;
    },
    downloadPDF() {
      // const flattenedData = this.flattenTree(this.items);
      // const doc = new jsPDF();
      // this.generatePDFContent(doc, flattenedData);
      // doc.save('data.pdf');
    },
    updateCombo(itemsOrigen, itemsDestino, condicion, combo) {
      itemsDestino.splice(0, itemsDestino.length);

      for (let item of itemsOrigen) {
        if (item.bus_id === condicion) {
          itemsDestino.push({
            value: item.value,
            text: item.text,
            routes: item.routes,
          });
        }
      }
      this.filters.location = this.itemsLocations2[0].value;

      return itemsDestino;
    },
    editItem(item) {
      this.selectedCategoryDgii = this.editedItem.dgii_category_id;
      this.editedIndex = 0;
      this.editedItem = Object.assign({}, item);

      this.seletecName = this.editedItem.code + " - " + this.editedItem.name;

      this.dialog = true;
    },
    close() {
      this.dialog = false;
    },
    changeBusiness(filters) {
      this.lastData = filters;
      let currency_id = this.$store.state.auth.bussines_units.find(
        (x) => x.id == this.lastData.business
      ).currency_id;
      this.selectedCurrecies = currency_id;

      this.periods = [];
      this.current_calendar = null;
      this.getCalendars(filters);
    },
    addAccount(item) {
      this.editedItem = [];
      this.itemSaved = [];
      this.selectedCategoryDgii = "";
      this.editedIndex = -1;
      let data = Object.assign({}, item);
      this.itemSaved = data;
      this.seletecName = data.name + " - " + data.code;
      this.editedItem.name = "";
      this.editedItem.code = "";
      this.dialog = true;
    },
  },
};
</script>

<style scoped>
.trial-link {
  cursor: pointer;
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 3px;
  transition: opacity 0.15s;
}
.trial-link:hover {
  opacity: 0.65;
}
.trial-tree .v-treeview-node__root {
  min-height: 36px;
}
.custom-field>>>.v-input__control {
  border-radius: 8px !important;
}

.custom-field>>>.v-input__slot {
  transition: all 0.3s ease;
}

.custom-field>>>.v-input__slot:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.border-2 {
  border-width: 2px !important;
}

.treeview-header {
  background-color: #f5f5f5;
  padding: 10px;
  margin-bottom: 10px;
}

.amount-chip {
  width: 80px;
  justify-content: center;
}
</style>
